import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  WORKSPACE_ART_LABELS,
  WORKSPACE_FARBE_VORGABE,
  WORKSPACE_FARBEN,
  type WorkspaceFarbe,
} from '@netcon-work/shared'
import { Check, Plus, Trash2, X } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { cn } from '../lib/cn'
import { personenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { FarbWahl, WorkspacePunkt } from './WorkspaceMarke'

/**
 * **Workspaces anlegen und zuteilen** – Z-07.11
 * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * Nils, 25.08.2026: *„Ich lege die an, das soll der Admin können."*
 *
 * ## Was hier bewusst *nicht* geht
 *
 * **Persönliche Workspaces bearbeiten.** Sie stehen in der Liste – mit Beschriftung, Besitzer
 * und zwei Zahlen –, aber ohne Mitgliederverwaltung und ohne Blick hinein. Der Admin muss
 * wissen, *dass* es sie gibt, um beim Ausscheiden aufräumen zu können
 * ([F-73](../../../../docs/OFFENE-FRAGEN.md)); alles darüber hinaus wäre der Bruch der Zusage
 * aus [E-232](../../../../docs/ENTSCHEIDUNGEN.md#e-232).
 *
 * Die Aufgabenzahl ist die Grenze, über die diese Ansicht nicht geht: *„12 Aufgaben"* sagt,
 * dass dort etwas liegt, und sagt nicht, was.
 *
 * **Die Art ändern.** Ein persönlicher Workspace, der teilbar wäre, ist kein Versprechen mehr;
 * ein geteilter, der plötzlich einer Person gehört, entzöge acht Leuten ihre Arbeit mit einem
 * Klick.
 */
export function WorkspaceVerwaltung() {
  const queryClient = useQueryClient()
  const { data: workspaces } = useQuery({
    queryKey: ['workspaces', 'alle'],
    queryFn: () => trpc.workspaces.alle.query(),
  })
  const { data: personen } = useQuery(personenQuery)
  const [offen, setOffen] = useState<string | null>(null)
  const [neu, setNeu] = useState<{ label: string; icon: string } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  /** Nach jeder Änderung **alles** verwerfen: der Umschalter liest dieselben Daten. */
  const frisch = () => void queryClient.invalidateQueries()
  const melde = (e: unknown) => setFehler(e instanceof Error ? e.message : 'Das ging nicht.')

  const anlegen = useMutation({
    mutationFn: (w: { label: string; icon: string }) => trpc.workspaces.anlegen.mutate(w),
    onSuccess: () => {
      setNeu(null)
      setFehler(null)
      frisch()
    },
    onError: melde,
  })
  const aendern = useMutation({
    mutationFn: (w: Parameters<typeof trpc.workspaces.aendern.mutate>[0]) =>
      trpc.workspaces.aendern.mutate(w),
    onSuccess: frisch,
    onError: melde,
  })
  /**
   * **Das Mail-Kürzel** (E-272). Eigene Prozedur, nicht `aendern`: den eigenen setzt der
   * Besitzer, den einer geteilten Welt der Admin – dieselbe Regel wie beim Aussehen, und sie
   * steht deshalb auch an derselben Art von Prozedur.
   */
  const kuerzelSetzen = useMutation({
    mutationFn: (w: { id: string; kuerzel: string | null }) =>
      trpc.workspaces.kuerzelSetzen.mutate(w),
    onSuccess: frisch,
    onError: melde,
  })
  const loeschen = useMutation({
    mutationFn: (w: { id: string; mitInhalt?: boolean }) => trpc.workspaces.loeschen.mutate(w),
    onSuccess: frisch,
    onError: melde,
  })

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      {fehler && (
        <div className="mb-3 rounded-lg bg-[#fdf1f1] px-3 py-2 text-[12.5px] text-pool-sofort">
          {fehler}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-[560px] w-full text-[13px]">
          <thead>
            <tr className="border-b border-line text-left text-[11px] text-muted uppercase">
              <th className="pb-2 font-semibold">Workspace</th>
              <th className="pb-2 font-semibold">Farbe</th>
              <th
                className="pb-2 font-semibold"
                title={'Im Betreff einer Mail an Work: „#NW8" schickt sie in diese Arbeitswelt'}
              >
                Kürzel
              </th>
              <th className="pb-2 font-semibold">Art</th>
              <th className="tabular pb-2 text-right font-semibold">Mitglieder</th>
              <th className="tabular pb-2 text-right font-semibold">Aufgaben</th>
              <th className="pb-2 text-center font-semibold" title="Wo Importe ohne Route landen">
                Auffang
              </th>
              <th className="pb-2 text-right font-semibold" />
            </tr>
          </thead>
          <tbody>
            {workspaces?.map((w) => (
              /**
               * **Der Schlüssel gehört ans Fragment, nicht an die Zeile darin.** Eine Zeile plus
               * eine aufgeklappte Mitgliederzeile sind zwei Geschwister je Workspace; sitzt der
               * Schlüssel innen, wirft React ihn beim Aufklappen weg und baut die Eingabefelder
               * neu – der Cursor springt beim Tippen aus dem WIP-Feld.
               */
              <Fragment key={w.id}>
                <tr className="border-b border-line/60">
                  <td className="py-2">
                    <span className="mr-1.5">{w.icon || '📁'}</span>
                    <span className={cn('font-semibold', !w.isActive && 'text-muted line-through')}>
                      {w.label}
                    </span>
                    <code className="ml-2 text-[11px] text-muted">{w.id}</code>
                  </td>
                  <td className="py-2">
                    {/**
                     * **Die Farbe setzt hier nur der Admin, und nur bei geteilten.**
                     *
                     * Ein persönlicher Workspace gehört jemandem; seine Farbe wählt er selbst im
                     * Umschalter (`workspaces.aussehenSetzen`). Der Admin sieht sie, damit er die
                     * Zeile wiedererkennt – anfassen soll er sie nicht.
                     */}
                    {w.art === 'geteilt' ? (
                      <FarbWahl
                        wert={
                          (WORKSPACE_FARBEN as readonly string[]).includes(w.farbe)
                            ? (w.farbe as WorkspaceFarbe)
                            : WORKSPACE_FARBE_VORGABE
                        }
                        onWahl={(farbe) => aendern.mutate({ id: w.id, farbe })}
                      />
                    ) : (
                      <WorkspacePunkt farbe={w.farbe} />
                    )}
                  </td>
                  <td className="py-2">
                    {/**
                     * **Das Kürzel für den Betreff** (E-272). Nils: *„damit könnte ich da Mails
                     * hinschicken, die aber die anderen … dann nicht sehen."*
                     *
                     * Gespeichert wird beim Verlassen des Feldes und nicht bei jedem Tastendruck:
                     * `NW` wäre auf dem Weg zu `NW8` ein eigener, gültiger Wert – und würde
                     * vergeben, bevor jemand zu Ende getippt hat.
                     */}
                    <KuerzelFeld
                      wert={w.kuerzel}
                      speichern={(kuerzel) => kuerzelSetzen.mutate({ id: w.id, kuerzel })}
                    />
                  </td>
                  <td className="py-2 text-muted">{WORKSPACE_ART_LABELS[w.art]}</td>
                  <td className="tabular py-2 text-right">{w.mitglieder}</td>
                  <td className="tabular py-2 text-right">{w.aufgaben}</td>
                  <td className="py-2 text-center">
                    {/**
                     * **Ein Radioknopf und keine Häkchenreihe.** „Auffang" ist eine Eigenschaft,
                     * die genau eine Zeile trägt – der Server nimmt sie beim Setzen allen anderen
                     * weg. Eine Reihe Häkchen behauptete, man könne zwei haben, und die Anzeige
                     * spränge nach dem Speichern zurück.
                     */}
                    <input
                      type="radio"
                      name="auffang"
                      checked={w.istStandard}
                      disabled={w.art === 'persoenlich' || !w.isActive}
                      onChange={() => aendern.mutate({ id: w.id, istStandard: true })}
                      title={
                        w.art === 'persoenlich'
                          ? 'Ein persönlicher Workspace kann kein Auffangbecken sein.'
                          : 'Hier landen Importe ohne Zuordnung.'
                      }
                    />
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {w.art === 'geteilt' && (
                      <button
                        type="button"
                        onClick={() => setOffen(offen === w.id ? null : w.id)}
                        className="rounded-lg px-2 py-1 text-[12px] font-semibold text-accent-deep hover:bg-accent-soft"
                      >
                        Mitglieder
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => aendern.mutate({ id: w.id, isActive: !w.isActive })}
                      className="ml-1 rounded-lg px-2 py-1 text-[12px] text-ink2 hover:bg-[#f3f2ef]"
                      title={
                        w.isActive
                          ? 'Verschwindet aus dem Umschalter, der Inhalt bleibt.'
                          : 'Wieder sichtbar machen'
                      }
                    >
                      {w.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    {w.aufgaben === 0 && w.art === 'geteilt' && (
                      <button
                        type="button"
                        onClick={() => loeschen.mutate({ id: w.id })}
                        className="ml-1 rounded-lg p-1.5 text-muted hover:bg-[#fdf1f1] hover:text-pool-sofort"
                        title="Löschen – geht nur, solange nichts drin liegt"
                      >
                        <Trash2 className="size-[15px]" />
                      </button>
                    )}
                    {/**
                     * **Ein persönlicher Workspace geht samt Inhalt** – [F-73], beantwortet am
                     * 26.08.2026. Ohne diesen Weg gäbe es keinen: „erst leeren" kann niemand
                     * befolgen, weil niemand hineinsehen darf. Deshalb steht die Zahl in der
                     * Rückfrage – sie ist alles, was der Admin über den Inhalt erfahren darf.
                     */}
                    {w.art === 'persoenlich' && (
                      <button
                        type="button"
                        onClick={() => {
                          const frage =
                            w.aufgaben === 0
                              ? `„${w.label}" endgültig löschen?`
                              : `„${w.label}" enthält ${w.aufgaben} Aufgaben. Alles endgültig ` +
                                'löschen? Das lässt sich nicht rückgängig machen.'
                          if (globalThis.confirm(frage))
                            loeschen.mutate({ id: w.id, mitInhalt: true })
                        }}
                        className="ml-1 rounded-lg p-1.5 text-muted hover:bg-[#fdf1f1] hover:text-pool-sofort"
                        title="Endgültig löschen – samt Inhalt, ohne hineinzusehen"
                      >
                        <Trash2 className="size-[15px]" />
                      </button>
                    )}
                  </td>
                </tr>
                {offen === w.id && (
                  <tr>
                    <td colSpan={8} className="bg-[#faf9f7] px-3 py-3">
                      <Mitglieder
                        workspaceId={w.id}
                        personen={personen ?? []}
                        onFehler={melde}
                        onGeaendert={frisch}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {neu ? (
        <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
          <input
            value={neu.icon}
            onChange={(e) => setNeu({ ...neu, icon: e.target.value })}
            className="w-12 rounded-lg border border-line px-2 py-1.5 text-center text-[15px]"
            aria-label="Symbol"
          />
          <input
            autoFocus
            value={neu.label}
            onChange={(e) => setNeu({ ...neu, label: e.target.value })}
            placeholder="Marketing"
            className="flex-1 rounded-lg border border-line px-3 py-1.5 text-[13px]"
            aria-label="Name des Workspace"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && neu.label.trim().length >= 2) anlegen.mutate(neu)
              if (e.key === 'Escape') setNeu(null)
            }}
          />
          <button
            type="button"
            disabled={neu.label.trim().length < 2 || anlegen.isPending}
            onClick={() => anlegen.mutate(neu)}
            className="rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-40"
          >
            <Check className="inline size-[14px]" /> Anlegen
          </button>
          <button
            type="button"
            onClick={() => setNeu(null)}
            className="rounded-lg p-1.5 text-muted hover:bg-[#f3f2ef]"
          >
            <X className="size-[15px]" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setNeu({ label: '', icon: '📁' })}
          className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-semibold text-accent-deep hover:bg-accent-soft"
        >
          <Plus className="size-[15px]" /> Geteilten Workspace anlegen
        </button>
      )}

      <p className="mt-3 text-[11.5px] leading-relaxed text-muted">
        Ein neuer Workspace ist <strong>leer und ohne Mitglieder</strong> – wer hineingehört, wird
        einzeln zugeteilt. Persönliche Workspaces legt jeder für sich selbst an; sie erscheinen hier
        nur mit Namen und Zahlen, nie mit Inhalt.
      </p>
    </div>
  )
}

/**
 * **Wer in diesem Workspace ist.**
 *
 * Gäste stehen nicht zur Wahl – sie sehen ohnehin nur, woran sie beteiligt sind, und eine
 * Mitgliedschaft erweiterte das nicht, zeigte sie aber in jeder Liste
 * ([E-237](../../../../docs/ENTSCHEIDUNGEN.md#e-237)).
 */
function Mitglieder({
  workspaceId,
  personen,
  onFehler,
  onGeaendert,
}: {
  workspaceId: string
  personen: { id: string; displayName: string; role: string }[]
  onFehler: (e: unknown) => void
  onGeaendert: () => void
}) {
  const { data: mitglieder } = useQuery({
    queryKey: ['workspaces', 'mitglieder', workspaceId],
    queryFn: () => trpc.workspaces.mitglieder.query({ workspaceId }),
  })

  const setzen = useMutation({
    mutationFn: (m: { personId: string; leitung: boolean; wipLimit: number | null }) =>
      trpc.workspaces.mitgliedSetzen.mutate({ workspaceId, ...m }),
    onSuccess: onGeaendert,
    onError: onFehler,
  })
  const entfernen = useMutation({
    mutationFn: (personId: string) =>
      trpc.workspaces.mitgliedEntfernen.mutate({ workspaceId, personId }),
    onSuccess: onGeaendert,
    onError: onFehler,
  })

  const drin = new Map((mitglieder ?? []).map((m) => [m.personId, m]))
  const waehlbar = personen.filter((p) => p.role !== 'gast')
  /**
   * **Warum hier nicht alle stehen, steht hier** (16.09.2026, Nils vor der Liste, in der alle
   * neun Häkchen schon gesetzt waren: *„ich kann keine Mitglieder hinzufügen."*). Die 25 Gäste
   * fehlen mit Absicht (E-237) – aber eine Liste, die stumm die Hälfte weglässt, sieht aus wie
   * eine, in der man nichts hinzufügen kann. Der Weg steht deshalb dran.
   */
  const gaeste = personen.length - waehlbar.length

  return (
    <div className="grid gap-1.5">
      {waehlbar.map((p) => {
        const m = drin.get(p.id)
        return (
          <div key={p.id} className="flex items-center gap-2 text-[12.5px]">
            <input
              type="checkbox"
              checked={Boolean(m)}
              onChange={(e) =>
                e.target.checked
                  ? setzen.mutate({ personId: p.id, leitung: false, wipLimit: null })
                  : entfernen.mutate(p.id)
              }
              id={`m-${workspaceId}-${p.id}`}
            />
            <label htmlFor={`m-${workspaceId}-${p.id}`} className="min-w-40 flex-1">
              {p.displayName}
            </label>
            {m && (
              <>
                <label className="flex items-center gap-1 text-muted">
                  <input
                    type="checkbox"
                    checked={m.leitung}
                    onChange={(e) =>
                      setzen.mutate({
                        personId: p.id,
                        leitung: e.target.checked,
                        wipLimit: m.wipLimit,
                      })
                    }
                  />
                  steuert hier
                </label>
                {/**
                 * **Leer heißt „das globale Limit gilt"** – und zählt dann über alle geteilten
                 * Workspaces. Eine Zahl hier ersetzt es und zählt nur diesen
                 * ([E-235](../../../../docs/ENTSCHEIDUNGEN.md#e-235),
                 * [E-238](../../../../docs/ENTSCHEIDUNGEN.md#e-238)).
                 */}
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={m.wipLimit ?? ''}
                  placeholder="WIP"
                  title="Eigenes WIP-Limit hier. Leer = das persönliche Limit gilt, über alle geteilten Workspaces."
                  onChange={(e) =>
                    setzen.mutate({
                      personId: p.id,
                      leitung: m.leitung,
                      wipLimit: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className="tabular w-16 rounded border border-line px-1.5 py-0.5 text-right"
                />
              </>
            )}
          </div>
        )
      })}
      {gaeste > 0 && (
        <p className="mt-1 text-[11px] text-muted">
          <strong className="text-ink2">
            {gaeste === 1 ? 'Ein Gast steht' : `${gaeste} Gäste stehen`} nicht zur Wahl.
          </strong>{' '}
          Wer hinein soll, bekommt zuerst unter <strong>Benutzer</strong> eine Rolle (Entwickler,
          Steuerer, Admin) – und ist damit von selbst Mitglied im Auffang-Workspace; hier lässt er
          sich dann auch in andere zuteilen.
        </p>
      )}
      <p className="mt-1 text-[11px] text-muted">
        Wer herausgenommen wird, sieht die Aufgaben dieses Workspace nicht mehr – auch nicht in
        „Mein Tag". Die Aufgaben selbst bleiben liegen.
      </p>
    </div>
  )
}

/**
 * **Das Kürzel-Feld** ([E-272](../../../../docs/ENTSCHEIDUNGEN.md#e-272), 28.08.2026).
 *
 * Drei bis acht Zeichen, Großschreibung erzwungen, mit dem `#` als blassem Vorsatz – so steht im
 * Feld, was man später in den Betreff tippt, und niemand muss raten, ob die Raute dazugehört.
 *
 * ## Warum beim Verlassen gespeichert wird und nicht bei jedem Zeichen
 *
 * Weil jeder Zwischenstand ein **gültiges** Kürzel ist: auf dem Weg zu `NW8` entsteht `NW`, und
 * das wäre vergeben, bevor jemand zu Ende getippt hat – womöglich an eine andere Arbeitswelt,
 * die es wirklich haben wollte. Gespeichert wird deshalb bei `blur` und bei Enter.
 */
function KuerzelFeld({
  wert,
  speichern,
}: {
  wert: string | null
  speichern: (kuerzel: string | null) => void
}) {
  const [text, setText] = useState(wert ?? '')

  /** Der Server ist die Wahrheit: kommt die Liste neu, gewinnt sie gegen den Tippstand. */
  useEffect(() => setText(wert ?? ''), [wert])

  const abschicken = () => {
    const neu = text.trim().toUpperCase()
    if (neu === (wert ?? '')) return
    /** Leer heißt „kein Kürzel" – und ist die einzige Länge unter drei, die erlaubt ist. */
    if (neu !== '' && neu.length < 3) {
      setText(wert ?? '')
      return
    }
    speichern(neu === '' ? null : neu)
  }

  return (
    <span className="inline-flex items-baseline gap-0.5">
      <span className="text-[11px] text-muted">#</span>
      <input
        value={text}
        maxLength={8}
        placeholder="—"
        aria-label="Kürzel für den Mail-Betreff"
        onChange={(e) => setText(e.target.value.toUpperCase())}
        onBlur={abschicken}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') setText(wert ?? '')
        }}
        className="w-24 rounded border border-line px-1 py-0.5 text-center font-mono text-[11.5px] uppercase"
      />
    </span>
  )
}
